import { useEffect, useMemo, useRef } from 'react';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import clsx from 'clsx';
import { Button } from 'common/components/Button';
import { Input } from 'common/components/Form/Input';
import { Select } from 'common/components/Form/Select';
import { Textarea } from 'common/components/Form/Textarea';
import { m } from 'framer-motion';
import { useRouter } from 'next/router';

import { EstimatorChipGroup } from './components/EstimatorChipGroup';
import { EstimatorOptionCard } from './components/EstimatorOptionCard';
import { EstimatorReportCard } from './components/EstimatorReportCard';
import {
  DEFAULT_SURFACE_VALUES,
  getAllowedCapabilityValues,
  PROJECT_TYPE_SURFACE_MAP,
} from './constants';
import { getEstimatorCopy } from './copy';
import { useProjectEstimator } from './useProjectEstimator';

export const ProjectEstimator: React.FC = () => {
  const hasMountedRef = useRef(false);
  const stepTopRef = useRef<HTMLDivElement | null>(null);
  const { locale } = useRouter();
  const language = locale === 'hr' ? 'hr' : 'en';
  const copy = getEstimatorCopy(language);
  const {
    currentStep,
    deliveryState,
    fieldError,
    formik,
    goBack,
    goNext,
    isGeneratingReport,
    report,
    selectProjectType,
    totalSteps,
    toggleArrayValue,
  } = useProjectEstimator(language);

  const isBackendPlatform = formik.values.projectType?.value === 'backend-platform';
  const isWebshop = formik.values.projectType?.value === 'webshop';
  const needsWebhookVolume = formik.values.keyCapabilities.includes('webhook-orchestration');
  const needsQueueVolume = formik.values.keyCapabilities.includes('background-jobs');

  const visibleCapabilities = useMemo(() => {
    const projectTypeValue = formik.values.projectType?.value;
    const allowedValues = getAllowedCapabilityValues(
      projectTypeValue,
      formik.values.preferredSurfaces,
    );

    return copy.options.capabilities.filter(option => allowedValues.includes(option.value));
  }, [
    copy.options.capabilities,
    formik.values.preferredSurfaces,
    formik.values.projectType?.value,
  ]);

  const visibleComplianceOptions = useMemo(
    () => (isBackendPlatform ? copy.options.backendCompliance : copy.options.compliance),
    [copy.options.backendCompliance, copy.options.compliance, isBackendPlatform],
  );

  const visibleSurfaces = useMemo(() => {
    const projectTypeValue = formik.values.projectType?.value;
    const allowedValues = projectTypeValue
      ? PROJECT_TYPE_SURFACE_MAP[projectTypeValue] || DEFAULT_SURFACE_VALUES
      : DEFAULT_SURFACE_VALUES;

    return copy.options.surfaces.filter(option => allowedValues.includes(option.value));
  }, [copy.options.surfaces, formik.values.projectType?.value]);

  const steps = useMemo(
    () =>
      isWebshop
        ? [
            copy.steps.projectDirection,
            copy.steps.productScope,
            copy.steps.webshopCommerce,
            copy.steps.operationsRisk,
            copy.steps.commercialFit,
            copy.steps.contactReport,
          ]
        : [
            copy.steps.projectDirection,
            copy.steps.productScope,
            copy.steps.operationsRisk,
            copy.steps.commercialFit,
            copy.steps.contactReport,
          ],
    [copy.steps, isWebshop],
  );
  const currentStepKey = useMemo(() => {
    if (isWebshop) {
      return [
        'projectDirection',
        'productScope',
        'webshopCommerce',
        'operationsRisk',
        'commercialFit',
        'contactReport',
      ][currentStep];
    }

    return ['projectDirection', 'productScope', 'operationsRisk', 'commercialFit', 'contactReport'][
      currentStep
    ];
  }, [currentStep, isWebshop]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
  }, [currentStep]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const allowedValues = new Set(visibleCapabilities.map(option => option.value));
    const nextSelected = formik.values.keyCapabilities.filter(value => allowedValues.has(value));

    if (nextSelected.length !== formik.values.keyCapabilities.length) {
      formik.setFieldValue('keyCapabilities', nextSelected, false);
    }
  }, [formik, formik.values.keyCapabilities, visibleCapabilities]);

  useEffect(() => {
    if (!isBackendPlatform && formik.values.apiEndpointCount) {
      formik.setFieldValue('apiEndpointCount', null, false);
    }

    if (!needsWebhookVolume && formik.values.webhookFlowCount) {
      formik.setFieldValue('webhookFlowCount', null, false);
    }

    if (!needsQueueVolume && formik.values.queueWorkflowCount) {
      formik.setFieldValue('queueWorkflowCount', null, false);
    }
  }, [
    formik,
    formik.values.apiEndpointCount,
    formik.values.queueWorkflowCount,
    formik.values.webhookFlowCount,
    isBackendPlatform,
    needsQueueVolume,
    needsWebhookVolume,
  ]);

  useEffect(() => {
    const allowedValues = new Set(visibleSurfaces.map(option => option.value));
    let nextSelected = formik.values.preferredSurfaces.filter(value => allowedValues.has(value));

    if (isBackendPlatform && !nextSelected.includes('backend-api')) {
      nextSelected = ['backend-api'];
    }

    if (nextSelected.length !== formik.values.preferredSurfaces.length) {
      formik.setFieldValue('preferredSurfaces', nextSelected, false);
    }
  }, [formik, formik.values.preferredSurfaces, isBackendPlatform, visibleSurfaces]);

  const renderProgress = () => (
    <div className="mb-2">
      <div className="mb-4 flex items-end justify-between text-sm uppercase text-primary">
        <span className="text-primary gap-2 flex items-end font-semibold">
          <span className="text-outline-primary-light text-2xl">0{String(currentStep + 1)}</span>
          {steps[currentStep]?.title}
        </span>
        <div className="flex items-center gap-2 hidden sm:flex">
          <button
            aria-label={copy.actions.back}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/80 transition-colors duration-300 hover:border-primary/60 hover:text-primary disabled:cursor-not-allowed disabled:opacity-35"
            disabled={currentStep === 0}
            type="button"
            onClick={goBack}>
            <IoChevronBack size={16} />
          </button>
          <span className="text-2xs min-w-[52px] text-center">
            {String(currentStep + 1)}/{String(totalSteps)}
          </span>
          <button
            aria-label={copy.actions.continue}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/80 transition-colors duration-300 hover:border-primary/60 hover:text-primary disabled:cursor-not-allowed disabled:opacity-35"
            disabled={currentStep >= totalSteps - 1}
            type="button"
            onClick={goNext}>
            <IoChevronForward size={16} />
          </button>
        </div>
      </div>
      <div className="mb-8 h-2 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className={clsx(
            'h-full rounded-tl-full rounded-bl-full bg-primary/90 transition-all duration-300',
            {
              'rounded-tr-full rounded-br-full': currentStep === totalSteps - 1,
            },
          )}
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );

  const renderFirstStepIntro = () => (
    <div className="mb-12">
      <div className="text-sm font-semibold uppercase text-primary">{copy.hero.eyebrow}</div>
      <h1
        className="max-w-5xl text-heading-hero"
        dangerouslySetInnerHTML={{ __html: copy.hero.title }}
      />
      <p className="mt-4 max-w-4xl">{copy.hero.description}</p>
      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        {copy.hero.bullets.map(item => (
          <div key={item} className="rounded-[20px] border border-white/10 p-5 text-white">
            {item}
          </div>
        ))}
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStepKey) {
      case 'projectDirection':
        return (
          <div className="grid gap-8">
            <div data-estimator-field="projectType">
              <div className="mb-5 text-md font-bold text-primary uppercase ml-3">
                {copy.labels.projectType}
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {copy.options.projectType.map(option => (
                  <EstimatorOptionCard
                    key={option.value}
                    description={option.description}
                    isSelected={formik.values.projectType?.value === option.value}
                    label={option.label}
                    onClick={() => selectProjectType(option)}
                    tooltip={option.tooltip}
                  />
                ))}
              </div>
              {!!fieldError('projectType') && (
                <div className="ml-2 mt-1.5 text-xs text-red-600">{fieldError('projectType')}</div>
              )}
            </div>

            {isBackendPlatform ? (
              <div className="rounded-[24px] border border-primary/20 bg-primary/5 p-5">
                {copy.labels.surfaces}
                <div className="mt-2 text-sm">{copy.labels.surfacesBackendOnlyNote}</div>
              </div>
            ) : (
              <EstimatorChipGroup
                errorMessage={fieldError('preferredSurfaces')}
                fieldName="preferredSurfaces"
                label={copy.labels.surfaces}
                onToggle={value => toggleArrayValue('preferredSurfaces', value)}
                options={visibleSurfaces}
                selectedValues={formik.values.preferredSurfaces}
              />
            )}

            <Textarea
              classNameTextarea="bg-white/5"
              required
              label={copy.labels.description}
              name="projectDescription"
              value={formik.values.projectDescription}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorMessage={fieldError('projectDescription')}
              helperText={copy.labels.descriptionHelp}
            />
          </div>
        );
      case 'productScope':
        return (
          <div className="grid gap-8">
            <div>
              <h2 className="text-4xl font-semibold text-white lg:text-5xl">
                {steps[currentStep]?.title}
              </h2>
              <p className="mt-1 max-w-3xl">{steps[currentStep]?.description}</p>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <Select
                classNameDropdownContainer="bg-white/5"
                options={copy.options.userRoles}
                required
                name="userRoles"
                label={copy.labels.userRoles}
                value={formik.values.userRoles}
                onChange={(value: unknown) => formik.setFieldValue('userRoles', value)}
                errorMessage={fieldError('userRoles')}
              />
              <Select
                classNameDropdownContainer="bg-white/5"
                options={copy.options.userCount}
                required
                name="estimatedUserCount"
                label={isWebshop ? copy.labels.userCountWebshop : copy.labels.userCount}
                value={formik.values.estimatedUserCount}
                onChange={(value: unknown) => formik.setFieldValue('estimatedUserCount', value)}
                errorMessage={fieldError('estimatedUserCount')}
              />
            </div>
            <EstimatorChipGroup
              errorMessage={fieldError('authenticationRequirements')}
              fieldName="authenticationRequirements"
              label={copy.labels.auth}
              onToggle={value => toggleArrayValue('authenticationRequirements', value)}
              options={copy.options.authentication}
              selectedValues={formik.values.authenticationRequirements}
            />
            <EstimatorChipGroup
              errorMessage={fieldError('keyCapabilities')}
              fieldName="keyCapabilities"
              label={copy.labels.keyCapabilities}
              onToggle={value => toggleArrayValue('keyCapabilities', value)}
              options={visibleCapabilities}
              selectedValues={formik.values.keyCapabilities}
            />
            {isBackendPlatform && (
              <div className="grid gap-3">
                <Select
                  classNameDropdownContainer="bg-white/5"
                  options={copy.options.apiEndpointCount}
                  required
                  name="apiEndpointCount"
                  label={copy.labels.apiEndpointCount}
                  value={formik.values.apiEndpointCount}
                  onChange={(value: unknown) => formik.setFieldValue('apiEndpointCount', value)}
                  errorMessage={fieldError('apiEndpointCount')}
                />
                {needsWebhookVolume && (
                  <Select
                    classNameDropdownContainer="bg-white/5"
                    options={copy.options.webhookFlowCount}
                    required
                    name="webhookFlowCount"
                    label={copy.labels.webhookFlowCount}
                    value={formik.values.webhookFlowCount}
                    onChange={(value: unknown) => formik.setFieldValue('webhookFlowCount', value)}
                    errorMessage={fieldError('webhookFlowCount')}
                  />
                )}
                {needsQueueVolume && (
                  <Select
                    classNameDropdownContainer="bg-white/5"
                    options={copy.options.queueWorkflowCount}
                    required
                    name="queueWorkflowCount"
                    label={copy.labels.queueWorkflowCount}
                    value={formik.values.queueWorkflowCount}
                    onChange={(value: unknown) => formik.setFieldValue('queueWorkflowCount', value)}
                    errorMessage={fieldError('queueWorkflowCount')}
                  />
                )}
              </div>
            )}
          </div>
        );
      case 'webshopCommerce':
        return (
          <div className="grid gap-8">
            <div>
              <h2 className="text-4xl font-semibold text-white lg:text-5xl">
                {steps[currentStep]?.title}
              </h2>
              <p className="mt-1 max-w-3xl">{steps[currentStep]?.description}</p>
            </div>
            <div data-estimator-field="implementationPathPreference">
              <div className="mb-5 ml-3 text-md font-bold uppercase text-primary">
                {copy.labels.implementationPathPreference}
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {copy.options.implementationPathPreference.map(option => (
                  <EstimatorOptionCard
                    key={option.value}
                    description={option.description}
                    isSelected={formik.values.implementationPathPreference?.value === option.value}
                    label={option.label}
                    onClick={() => formik.setFieldValue('implementationPathPreference', option)}
                    tooltip={option.tooltip}
                  />
                ))}
              </div>
              {!!fieldError('implementationPathPreference') && (
                <div className="ml-2 mt-1.5 text-xs text-red-600">
                  {fieldError('implementationPathPreference')}
                </div>
              )}
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <Select
                classNameDropdownContainer="bg-white/5"
                options={copy.options.adminExperiencePreference}
                required
                name="adminExperiencePreference"
                label={copy.labels.adminExperiencePreference}
                value={formik.values.adminExperiencePreference}
                onChange={(value: unknown) =>
                  formik.setFieldValue('adminExperiencePreference', value)
                }
                errorMessage={fieldError('adminExperiencePreference')}
              />
              <Select
                classNameDropdownContainer="bg-white/5"
                options={copy.options.commerceLogicComplexity}
                required
                name="commerceLogicComplexity"
                label={copy.labels.commerceLogicComplexity}
                value={formik.values.commerceLogicComplexity}
                onChange={(value: unknown) =>
                  formik.setFieldValue('commerceLogicComplexity', value)
                }
                errorMessage={fieldError('commerceLogicComplexity')}
              />
              <Select
                classNameDropdownContainer="bg-white/5"
                options={copy.options.productModel}
                required
                name="productModel"
                label={copy.labels.productModel}
                value={formik.values.productModel}
                onChange={(value: unknown) => formik.setFieldValue('productModel', value)}
                errorMessage={fieldError('productModel')}
              />
              <Select
                classNameDropdownContainer="bg-white/5"
                options={copy.options.catalogSize}
                required
                name="catalogSize"
                label={copy.labels.catalogSize}
                value={formik.values.catalogSize}
                onChange={(value: unknown) => formik.setFieldValue('catalogSize', value)}
                errorMessage={fieldError('catalogSize')}
              />
              <Select
                classNameDropdownContainer="bg-white/5"
                options={copy.options.catalogComplexity}
                required
                name="catalogComplexity"
                label={copy.labels.catalogComplexity}
                value={formik.values.catalogComplexity}
                onChange={(value: unknown) => formik.setFieldValue('catalogComplexity', value)}
                errorMessage={fieldError('catalogComplexity')}
              />
              <Select
                classNameDropdownContainer="bg-white/5"
                options={copy.options.checkoutComplexity}
                required
                name="checkoutComplexity"
                label={copy.labels.checkoutComplexity}
                value={formik.values.checkoutComplexity}
                onChange={(value: unknown) => formik.setFieldValue('checkoutComplexity', value)}
                errorMessage={fieldError('checkoutComplexity')}
              />
            </div>
            <Select
              classNameDropdownContainer="bg-white/5"
              options={copy.options.inventoryComplexity}
              required
              name="inventoryComplexity"
              label={copy.labels.inventoryComplexity}
              value={formik.values.inventoryComplexity}
              onChange={(value: unknown) => formik.setFieldValue('inventoryComplexity', value)}
              errorMessage={fieldError('inventoryComplexity')}
            />
          </div>
        );
      case 'operationsRisk':
        return (
          <div className="grid gap-8">
            <div>
              <h2 className="text-4xl font-semibold text-white lg:text-5xl">
                {steps[currentStep]?.title}
              </h2>
              <p className="mt-1 max-w-3xl">{steps[currentStep]?.description}</p>
            </div>
            <EstimatorChipGroup
              errorMessage={fieldError('externalIntegrations')}
              fieldName="externalIntegrations"
              label={copy.labels.integrations}
              onToggle={value => toggleArrayValue('externalIntegrations', value)}
              options={copy.options.integrations}
              selectedValues={formik.values.externalIntegrations}
            />
            <Select
              options={copy.options.migration}
              required
              name="existingSystemMigration"
              label={copy.labels.migration}
              value={formik.values.existingSystemMigration}
              onChange={(value: unknown) => formik.setFieldValue('existingSystemMigration', value)}
              errorMessage={fieldError('existingSystemMigration')}
            />
            <EstimatorChipGroup
              errorMessage={fieldError('complianceRequirements')}
              fieldName="complianceRequirements"
              label={isBackendPlatform ? copy.labels.backendCompliance : copy.labels.compliance}
              onToggle={value => toggleArrayValue('complianceRequirements', value)}
              options={visibleComplianceOptions}
              selectedValues={formik.values.complianceRequirements}
            />
          </div>
        );
      case 'commercialFit':
        return (
          <div className="grid gap-8">
            <div>
              <h2 className="text-4xl font-semibold text-white lg:text-5xl">
                {steps[currentStep]?.title}
              </h2>
              <p className="mt-1 max-w-3xl">{steps[currentStep]?.description}</p>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <Select
                options={copy.options.timeline}
                required
                name="expectedTimeline"
                label={copy.labels.timeline}
                value={formik.values.expectedTimeline}
                onChange={(value: unknown) => formik.setFieldValue('expectedTimeline', value)}
                errorMessage={fieldError('expectedTimeline')}
              />
              <Select
                options={copy.options.budget}
                required
                name="approximateBudgetRange"
                label={copy.labels.budget}
                value={formik.values.approximateBudgetRange}
                onChange={(value: unknown) => formik.setFieldValue('approximateBudgetRange', value)}
                errorMessage={fieldError('approximateBudgetRange')}
              />
            </div>
            <div className="rounded-[24px] border border-primary/20 bg-primary/5 p-6">
              {copy.disclaimer}
            </div>
          </div>
        );
      case 'contactReport':
        return (
          <div className="grid gap-8">
            <div>
              <h2 className="text-4xl font-semibold text-white lg:text-5xl">
                {steps[currentStep]?.title}
              </h2>
              <p className="mt-1 max-w-3xl">{copy.report.finalStepIntro}</p>
            </div>
            <Input
              required
              label={copy.labels.name}
              name="contactName"
              value={formik.values.contactName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorMessage={fieldError('contactName')}
            />
            <div className="grid gap-3 lg:grid-cols-2">
              <Input
                required
                label={copy.labels.email}
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={fieldError('email')}
              />
              <Input
                label={copy.labels.phone}
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={fieldError('phoneNumber')}
              />
            </div>
            <Input
              label={copy.labels.company}
              name="companyName"
              value={formik.values.companyName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorMessage={fieldError('companyName')}
            />
            <Input
              label={copy.labels.whereFrom}
              name="howDidYouHearAboutUs"
              value={formik.values.howDidYouHearAboutUs}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorMessage={fieldError('howDidYouHearAboutUs')}
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (report) {
    return (
      <section className="bg-gradient relative min-h-screen overflow-hidden px-6 pb-20 pt-32 lg:px-10 lg:pb-24 lg:pt-36">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10">
            <div className="text-sm font-semibold uppercase text-primary">
              {copy.report.finalThankYouEyebrow}
            </div>
            <h1 className="text-4xl font-semibold text-white lg:text-6xl">
              {copy.report.finalThankYouTitle}
            </h1>
            <p className="mt-4 max-w-4xl">{copy.report.finalThankYouDescription}</p>
          </div>

          <EstimatorReportCard deliveryState={deliveryState} language={language} report={report} />

          <div className="mt-10 rounded-[32px] border border-white/10 bg-black/10 p-6 lg:p-8">
            <div className="cursor-pointer list-none text-sm font-semibold uppercase text-primary">
              {copy.report.costHeading}
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {copy.costDrivers.map(item => (
                <div
                  key={item.key}
                  className="rounded-[24px] border border-white/10 bg-white/5 p-5">
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="mt-3 text-md">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient relative min-h-screen overflow-hidden px-6 pb-20 pt-28 lg:px-10 lg:pb-24 lg:pt-32">
      <div ref={stepTopRef} />
      <div className="mx-auto flex min-h-[calc(100vh-9rem)] max-w-7xl flex-col justify-between">
        <form className="flex min-h-full flex-1 flex-col" onSubmit={formik.handleSubmit}>
          <div className="m-0 hidden h-0 w-0 p-0">
            <Input
              label={copy.labels.about}
              name="about"
              value={formik.values.about}
              onChange={formik.handleChange}
            />
          </div>

          {renderProgress()}

          {currentStep === 0 && renderFirstStepIntro()}

          <div className="flex flex-1 items-start">
            <div className="w-full rounded-[32px] border border-white/10 bg-white/5 p-6 lg:p-10">
              <m.div
                key={currentStep}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24, ease: 'easeOut' }}>
                {renderStepContent()}
              </m.div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between">
                <Button
                  button
                  className="border-white text-white items-center justify-center"
                  disabled={currentStep === 0}
                  onClick={goBack}
                  showArrow={false}
                  type="outline">
                  {copy.actions.back}
                </Button>

                {currentStep < totalSteps - 1 ? (
                  <Button
                    className="w-full justify-center sm:w-auto"
                    button="button"
                    onClick={goNext}>
                    {copy.actions.continue}
                  </Button>
                ) : (
                  <Button
                    className="w-full justify-center sm:w-auto"
                    button="submit"
                    loading={isGeneratingReport}>
                    {copy.actions.generateEstimate}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};
